import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, Plus, Trash2, Pencil, X, Star } from 'lucide-react';
import { toast } from 'sonner';
import { testimonialsAPI } from '../../services/api';

const TestimonialsEditor = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', role: '', text: '', rating: 5, order: 1 });

  const load = useCallback(async () => {
    try { const res = await testimonialsAPI.getAll(); setItems(res.data || []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const resetForm = () => { setEditing(null); setForm({ name: '', company: '', role: '', text: '', rating: 5, order: items.length + 1 }); };

  const startEdit = (t) => {
    setEditing(t._id);
    setForm({ name: t.name || '', company: t.company || '', role: t.role || '', text: t.text || '', rating: t.rating || 5, order: t.order || 1 });
  };

  const handleSave = async () => {
    if (!form.name || !form.text) { toast.error('Name and text are required'); return; }
    const payload = { ...form, rating: Number(form.rating), order: Number(form.order) };
    try {
      if (editing === 'new') {
        await testimonialsAPI.create(payload);
        toast.success('Testimonial added');
      } else {
        await testimonialsAPI.update(editing, payload);
        toast.success('Testimonial updated');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try { await testimonialsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Manage customer testimonials shown on the homepage.</p>
        <Button onClick={() => { setEditing('new'); setForm({ name: '', company: '', role: '', text: '', rating: 5, order: items.length + 1 }); }} data-testid="add-testimonial">
          <Plus className="w-4 h-4 mr-1" /> Add Testimonial
        </Button>
      </div>

      {editing && (
        <Card className="border-emerald-200" data-testid="testimonial-form">
          <CardHeader><CardTitle>{editing === 'new' ? 'New Testimonial' : 'Edit Testimonial'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1 block">Company</label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1 block">Role</label><Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} /></div>
            </div>
            <div><label className="text-sm font-medium text-gray-700 mb-1 block">Testimonial Text *</label><Textarea rows={3} value={form.text} onChange={e => setForm({...form, text: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-gray-700 mb-1 block">Rating (1-5)</label><Input type="number" min="1" max="5" value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1 block">Order</label><Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave}><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {items.map(t => (
        <Card key={t._id}>
          <CardContent className="flex items-start justify-between py-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900">{t.name}</h3>
                <span className="text-sm text-gray-500">- {t.role}, {t.company}</span>
              </div>
              <p className="text-gray-600 text-sm italic">"{t.text}"</p>
              <div className="flex items-center gap-1 mt-1">{Array.from({length: t.rating || 5}).map((_, i) => <Star key={`star-${i}`} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}</div>
            </div>
            <div className="flex gap-2 ml-4">
              <Button size="sm" variant="outline" onClick={() => startEdit(t)}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(t._id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default TestimonialsEditor;
